import React, { Dispatch, SetStateAction, useState } from "react";
import { Header } from "@/components/header";
import useMeter from "@/meters/useMeter";
import MeterAssetsTable from "@/meters/MeterAssetsTable";
import { Card } from "@/components/ui/card";
import ThaloIcon from "@/components/thalo-icon";
import { ChartCard } from "@/components/chart-card";
import { ONE_HOUR } from "@/dateRanges/windowSizes";
import { LAST_24_HOURS, LAST_30_DAYS, THIS_YEAR_SO_FAR } from "@/dateRanges/dateRanges";
import { formatDistance, parseISO } from "date-fns";
import { meterQuantityUnitId, meterQuantityUnits } from "@/meters/meter";
import { ToggleGroup, ToggleItem } from "@/components/ui/toggle-group";
import useLatestMeterReading from "@/meters/useLatestMeterReading";
import { DatumCardQuery } from "@/components/datum-card-query";
import MeterLocation from "@/assets/meter_location.png";
import { useDateRange } from "@/dateRanges/DateRangeContext";
import { getDatum, SeriesDatumName } from "@/components/charts/lineChartResponse";

const Last24hUsageCard = ({ meterId, unit }: { meterId: any, unit: meterQuantityUnitId}) => (
  <DatumCardQuery
    className="grow"
    forceLoading={!meterId}
    title="Usage last 24h"
    apiPath={`meters/${meterId}/usage-over-time/`}
    suffix={meterQuantityUnits[unit]}
    accessorFn={getDatum("overall", SeriesDatumName.LATEST)}
    decimalPoints={3}
    query={{
      windowSize: ONE_HOUR,
      dateRange: LAST_24_HOURS,
      target_unit: unit
    }}
    icon="Clock Check"
  />
);

const EstimatedLastMonthSpendCard = ({ meterId }: { meterId: any }) => (
  <DatumCardQuery
    className="grow"
    forceLoading={!meterId}
    title="Estimated spend last month"
    apiPath={`meters/${meterId}/usage-over-time/`}
    suffix={meterQuantityUnits["USD"]}
    accessorFn={getDatum("overall", SeriesDatumName.LATEST)}
    decimalPoints={2}
    query={{
      windowSize: ONE_HOUR,
      dateRange: LAST_30_DAYS,
      target_unit: "USD"
    }}
    icon="Money"
  />
);

const FuelUsage2024Card = (
  {
    unit,
    meterId,
    setSelectedUnit
  }: {
    unit: meterQuantityUnitId,
    meterId: any,
    setSelectedUnit: Dispatch<SetStateAction<meterQuantityUnitId>>
  }) => (
  <ChartCard
    key={`fuel-${meterQuantityUnits}`}
    className="col-span-12 flex-1 lg:col-span-6"
    title={
      <div className="flex items-center">
        <div>2024 Fuel Usage</div>
        <UnitSwitcher className="pl-8 font-normal" selectedUnit={unit} setSelectedUnit={setSelectedUnit}/>
      </div>
    }
    unit={meterQuantityUnits[unit]}
    apiPath={`meters/${meterId}/usage-over-time/`}
    query={[{
      windowSize: ONE_HOUR,
      dateRange: THIS_YEAR_SO_FAR,
      target_unit: unit
    }]}
    yAxisName={`2024 Fuel Usage (${meterQuantityUnits[unit]})`}
  />
);

const LatestMeterReadingCard = ({ meterId }: { meterId: any }) => {
  const latestReading = useLatestMeterReading(meterId)
  const timeDistance = latestReading.data?.data ? formatDistance(parseISO(latestReading.data.data.timestamp), new Date(), { addSuffix: true }) : ""
  return (
    <Card className="p-4">
      <div className="flex mb-4">
        <ThaloIcon iconName="Gauge" width="w-12" height="h-12"/>
        <div className="ml-2">
          <div className="flex items-baseline">
            <h2 className="font-barlow font-semibold text-xl">Latest meter reading</h2>
            <span className="ml-2">({latestReading.data ? meterQuantityUnits[latestReading?.data?.data.value.units] : ""})</span>
          </div>
          <p>
            Updated:{' '}
            <em>
              {
                !latestReading ? (
                  <span className="blur-sm">seconds ago</span>
                ) : (
                  <span>{timeDistance}</span>
                )
              }
            </em>
          </p>
        </div>
      </div>
      <div className="bg-black text-white font-barlow font-bold text-6xl tracking-widest px-2 py-1 w-full text-right">
        {
          !latestReading.data ? (
            <span className="blur-md">000000</span>
          ) : (
            <span>{Math.round(latestReading?.data?.data.value.magnitude * 10) / 10}</span>
          )
        }
      </div>
    </Card>
  );
};

const MeterLocationCard = () => (
  <Card className="p-4 mb-4 grow">
    <div className="flex mb-4 items-center">
      <ThaloIcon iconName="Hexagon" width="w-12" height="h-12"/>
      <h2 className="ml-2 font-barlow font-semibold text-xl">Meter location</h2>
    </div>
    <div>
      <img alt="Meter location diagram" src={MeterLocation} />
    </div>
  </Card>
);

const UnitSwitcher = (
  {
    selectedUnit,
    setSelectedUnit,
    className
  }: {
    selectedUnit: meterQuantityUnitId,
    setSelectedUnit: Dispatch<SetStateAction<meterQuantityUnitId>>,
    className: string
  }) => (
  <ToggleGroup className={className} type="single" value={selectedUnit} onValueChange={(value: meterQuantityUnitId) => {
    if (value) setSelectedUnit(value)
  }}>
    {
      Object.entries(meterQuantityUnits).map(([id, label]) => (
        <ToggleItem key={id} value={id}>{label}</ToggleItem>
      ))
    }
  </ToggleGroup>
)

const MeterPage = ({ meterId }: { meterId: number }) => {
  const meter = useMeter(meterId);
  useDateRange({ overrideDefaultDateRange: THIS_YEAR_SO_FAR });

  const [selectedUnit, setSelectedUnit] = useState<meterQuantityUnitId>("kilostandard_cubic_foot");

  return (
    <>
      <Header
        name={`Meter: ${meter.data?.data.name}`}
        showDatePicker={false}
        className="font-normal text-4xl"
      />
      <div>
        <p>
          Thalo is currently monitoring this meter with Thalo's AI Vision Engine, providing you with new data for
          smarter emissions management.
        </p>
      </div>
      <div className="grid grid-cols-12 gap-4 items-stretch">
        <div className="col-span-12 md:col-span-8 flex flex-col">
          <div className="flex gap-4 items-start mb-4">
            <Last24hUsageCard meterId={meterId} unit={selectedUnit}/>
            <EstimatedLastMonthSpendCard meterId={meterId} />
          </div>
          <FuelUsage2024Card meterId={meterId} unit={selectedUnit} setSelectedUnit={setSelectedUnit} />
        </div>
        <div className="col-span-12 md:col-span-4 flex flex-col">
          <MeterLocationCard />
          <LatestMeterReadingCard meterId={meterId} />
        </div>
      </div>
      <MeterAssetsTable meterId={meterId}/>
    </>
  )
}

export default MeterPage;